<template>
  <BaseModalEditCreate
    :title="$t('representative.fieldMapping.rowMapping')"
    :is-modified="isModified"
    @dismiss-changes="discardChanges"
    @save="saveRowMapping">
    <template #form>
      <div class="h-96 flex flex-col">
        <BaseTable
          table-id="row-mapping"
          :headers="headers"
          :list="localMappingFields"
          is-saving-layout-hidden
          is-search-hidden
          is-refresh-button-hidden
          is-download-button-hidden
          :is-add-new-hidden="props.mode === 'view'"
          hide-grouping
          :edit-mode="props.mode !== 'view'"
          disable-multiselect
          @add-new="onMappingFieldAdd">
          <template #custom="{ header, item }">
            <!-- CSV properties -->
            <BaseInput
              v-if="
                mappingType === MappingType.CSV &&
                (header === 'ColumnName' || header === 'ColumnIndex')
              "
              class="h-8 bg-white rounded-md"
              color="light"
              :model-value="item.CSV[header]"
              :disabled="props.mode === 'view'"
              @update:model-value="updateProperty(item, `CSV.${header}`, $event)" />

            <!-- FixedLength properties -->
            <BaseInput
              v-else-if="
                mappingType === MappingType.FixedLength &&
                (header === 'StartPosition' || header === 'Length')
              "
              class="h-8 bg-white rounded-md"
              color="light"
              type="number"
              :model-value="item.FixedLength[header]"
              :disabled="props.mode === 'view'"
              @update:model-value="
                updateProperty(
                  item,
                  `FixedLength.${header}`,
                  $event ? parseInt(String($event)) : null
                )
              " />

            <!-- JSON properties -->
            <BaseInput
              v-else-if="mappingType === MappingType.JSON && header === 'JsonPath'"
              class="h-8 bg-white rounded-md"
              color="light"
              :model-value="item.JSON.JsonPath"
              :disabled="props.mode === 'view'"
              @update:model-value="updateProperty(item, 'JSON.JsonPath', $event)" />

            <!-- Database properties -->
            <BaseInput
              v-else-if="mappingType === MappingType.Database && header === 'ColumnName'"
              class="h-8 bg-white rounded-md"
              color="light"
              :model-value="item.Database.ColumnName"
              :disabled="props.mode === 'view'"
              @update:model-value="updateProperty(item, 'Database.ColumnName', $event)" />
          </template>

          <!-- Actions column -->
          <template #actions="{ item }">
            <div class="flex flex-col items-center gap-1">
              <button
                type="button"
                class="p-1.5 text-gray-500 rounded-full hover:bg-gray-100"
                @click="discardMappingField(item)"
                v-if="isItemModified(item) && props.mode !== 'view'">
                <IconXmark class="w-4 h-4" />
              </button>
              <button
                type="button"
                class="p-1.5 text-red-400 rounded-full hover:bg-red-50"
                @click="deleteMappingField(item)"
                v-if="!isItemModified(item) && props.mode !== 'view'">
                <IconTrashOutline class="w-4 h-4" />
              </button>
            </div>
          </template>
        </BaseTable>
      </div>
    </template>
  </BaseModalEditCreate>
</template>

<script setup lang="ts">
import type { MappingField } from '@/types/RecordMapping';
import { MappingType } from '@/types/RecordMapping';

import IconTrashOutline from '@/assets/icons/IconTrashOutline.vue';
import IconXmark from '@/assets/icons/IconXmark.vue';
import BaseInput from '@/components/common/BaseInput.vue';
import BaseTable from '@/components/common/BaseTable.vue';
import BaseModalEditCreate from '@/components/common/BaseModalEditCreate.vue';
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

const { t: $t } = useI18n();

const props = defineProps<{
  mappingFields: MappingField[];
  mappingType: MappingType;
  mode?: 'view' | 'edit';
}>();

const emit = defineEmits<{
  close: [];
  save: [mappingFields: MappingField[]];
}>();

const localMappingFields = ref<MappingField[]>([]);
const originalMappingFields = ref<MappingField[]>([]);

watch(
  () => props.mappingFields,
  (newFields) => {
    const deepCopy = JSON.parse(JSON.stringify(newFields || []));
    localMappingFields.value = deepCopy;
    originalMappingFields.value = JSON.parse(JSON.stringify(deepCopy));
  },
  { immediate: true }
);

const isModified = computed(
  () => JSON.stringify(localMappingFields.value) !== JSON.stringify(originalMappingFields.value)
);

// Check if an item is modified or new
function isItemModified(item: MappingField): boolean {
  const index = localMappingFields.value.findIndex((field) => field === item);
  if (index < 0) return false;

  const originalItem = originalMappingFields.value[index];
  if (!originalItem) return true; // New item (index beyond original array)

  return JSON.stringify(item) !== JSON.stringify(originalItem);
}

const headers = computed(() => {
  const baseHeaders = [];

  if (props.mappingType === MappingType.CSV) {
    baseHeaders.push(
      {
        value: 'ColumnName',
        text: $t('representative.fieldMapping.csvColumnName'),
        type: 'custom',
        active: true,
        hasInlineEdit: true,
      },
      {
        value: 'ColumnIndex',
        text: $t('representative.fieldMapping.csvColumnIndex'),
        type: 'custom',
        active: true,
        hasInlineEdit: true,
      }
    );
  } else if (props.mappingType === MappingType.FixedLength) {
    baseHeaders.push(
      {
        value: 'StartPosition',
        text: $t('representative.fieldMapping.fixedLengthStartPosition'),
        type: 'custom',
        active: true,
        hasInlineEdit: true,
      },
      {
        value: 'Length',
        text: $t('representative.fieldMapping.fixedLengthLength'),
        type: 'custom',
        active: true,
        hasInlineEdit: true,
      }
    );
  } else if (props.mappingType === MappingType.JSON) {
    baseHeaders.push({
      value: 'JsonPath',
      text: $t('representative.fieldMapping.jsonPath'),
      type: 'custom',
      active: true,
      hasInlineEdit: true,
    });
  } else if (props.mappingType === MappingType.Database) {
    baseHeaders.push({
      value: 'ColumnName',
      text: $t('representative.fieldMapping.databaseColumnName'),
      type: 'custom',
      active: true,
      hasInlineEdit: true,
    });
  }

  baseHeaders.push({
    value: 'actions',
    text: $t('baseObject.actions'),
    active: true,
  });

  return baseHeaders;
});

function updateProperty(item: MappingField, propertyPath: string, value: any) {
  // Handle nested property paths like 'CSV.ColumnName' or 'FixedLength.StartPosition'
  const parts = propertyPath.split('.');
  if (parts.length === 2) {
    const [parent, child] = parts;
    (item as any)[parent][child] = value ?? (typeof value === 'number' ? value : '');
  }
}

function discardMappingField(item: MappingField) {
  const index = localMappingFields.value.findIndex((field) => field === item);
  if (index < 0) return;

  const originalItem = originalMappingFields.value[index];

  if (originalItem) {
    // Restore from original (edited existing item)
    Object.assign(item, JSON.parse(JSON.stringify(originalItem)));
  } else {
    // No original = newly added item, remove it
    localMappingFields.value.splice(index, 1);
  }
}

function deleteMappingField(item: MappingField) {
  const index = localMappingFields.value.findIndex((field) => field === item);
  if (index >= 0) {
    localMappingFields.value.splice(index, 1);
    // Also remove from originals to keep indices aligned
    if (index < originalMappingFields.value.length) {
      originalMappingFields.value.splice(index, 1);
    }
  }
}

function onMappingFieldAdd() {
  const newField: MappingField = {
    CSV: { ColumnIndex: null, ColumnName: '' },
    FixedLength: { StartPosition: null, Length: null },
    JSON: { JsonPath: '' },
    Database: { ColumnName: '' },
    RestApi: {},
    XLXS: { ColumnName: '' },
  };

  localMappingFields.value.push(newField);
}

function saveRowMapping() {
  emit('save', [...localMappingFields.value]);
}

function discardChanges() {
  emit('close');
}
</script>
